import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import AmbientBackground from '../common/AmbientBackground';
import { supabase } from '../../config/supabase';

interface ProfileImage {
  image_url: string;
  description: string;
}

const AboutPage: React.FC = () => {
  const [profileImage, setProfileImage] = useState<ProfileImage | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const { data, error } = await supabase
          .from('public_assets')
          .select('image_url, description')
          .eq('category', 'about-section')
          .eq('visible', true)
          .order('sort_order', { ascending: true })
          .limit(1)
          .single();

        if (error) throw error;
        setProfileImage(data);
      } catch (error) {
        console.error('Error fetching profile image:', error);
      }
    };

    fetchProfileImage();
  }, []);

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-light.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-8">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment mb-8">
            About Tara
          </h1>

          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8 text-ink dark:text-parchment/60">
            <div className="flow-root">
              {profileImage && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="float-right ml-6 mb-2 relative group"
                >
                  <div className="w-32 md:w-48 aspect-[2/3] rounded-lg overflow-hidden shadow-lg border-2 border-vellum dark:border-dusk bg-vellum dark:bg-shadow transform transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-xl">
                    <img
                      src={profileImage.image_url}
                      alt={profileImage.description}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </button>
              )}
              <p className="mb-8">
                Hi, I'm Tara, creator of Tara's Tarot ✨
              </p>

              <p className="mb-8">
                This app began as a personal project, a quiet space to land and store readings. Tarot has been an enlightening journey for me and a way to slow down, reflect, and make meaning out of life's beautiful (and sometimes painful) messes. I'm not a professional tarot reader; I'm a curious, grounded, spiritually inclined human who believes in intuition, growth, and keeping it real.
              </p>

              <p className="mb-8">
                You'll find this space to be both mystical and practical — a blend of candlelight and clarity. It's here for you to explore your own voice and inner knowing, with a little help from the cards.
              </p>

              <p className="mb-8">
                Thanks for being here. You're always welcome to learn, lean into your intuition, and have a little fun as well 🪩
              </p>

              <p className="font-display text-2xl">
                Enjoy!
              </p>
            </div>
          </div>
        </div>

        {/* Expanded Image Modal */}
        {isExpanded && profileImage && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setIsExpanded(false)}
          >
            <div
              className="relative max-w-4xl w-full max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <img
                src={profileImage.image_url}
                alt={profileImage.description}
                className="w-full h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setIsExpanded(false)}
                className="absolute top-4 right-4 text-white hover:text-mist transition-colors"
              >
                <span className="text-2xl leading-none">×</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AmbientBackground>
  );
};

export default AboutPage;
